import { type ChartOptions, type ScriptableContext } from "chart.js";

import { remToPx } from "@/utils/sizes";
import {
  getThemeColor,
  getThemeFontSans,
  getThemeTextSize,
} from "@/utils/theme-tokens";

const PADDING = 44;

const isPointContext = (ctx: ScriptableContext<"line">) => {
  return typeof ctx.dataIndex === "number" && ctx.dataIndex !== null;
};
const isLastPoint = (ctx: ScriptableContext<"line">) => {
  if (!isPointContext(ctx)) return false;

  const dataset = ctx.chart.data.datasets?.[ctx.datasetIndex];
  const data = (dataset?.data || []) as number[];

  return ctx.dataIndex === data.length - 1;
};

const yAxisTopPlugin = (theme: "dark" | "light") => ({
  id: "yAxisTopPlugin",
  beforeDatasetsDraw(chart: any) {
    const yScale = chart.scales.y;
    if (!yScale) return;

    const { ctx, chartArea } = chart;
    const { top, bottom, left, right } = chartArea;

    ctx.save();
    ctx.strokeStyle =
      theme === "dark" ? getThemeColor("gray-800") : getThemeColor("gray-300");
    ctx.lineWidth = 1;

    yScale.ticks.forEach((tick: any, index: number) => {
      const y = yScale.getPixelForTick(index);
      if (y < top || y > bottom) return;

      ctx.beginPath();
      ctx.moveTo(left, y);
      ctx.lineTo(right + PADDING, y);
      ctx.stroke();
    });

    ctx.restore();
  },

  afterDatasetsDraw(chart: any) {
    const yScale = chart.scales.y;
    if (!yScale) return;

    const { ctx, chartArea } = chart;
    const { right } = chartArea;

    ctx.save();
    ctx.font = `${getThemeTextSize("sm")} ${getThemeFontSans()}`;
    ctx.fillStyle =
      theme === "dark" ? getThemeColor("gray-700") : getThemeColor("gray-500");
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";

    yScale.ticks.forEach((tick: any, index: number) => {
      const y = yScale.getPixelForTick(index) - 10;
      const x = right + PADDING;

      ctx.fillText(tick.label, x, y);
    });

    ctx.restore();
  },
});

const legendSpacingPlugin = {
  id: "legendSpacing",
  beforeInit(chart: any) {
    const originalFit = chart.legend.fit;

    chart.legend.fit = function fit() {
      originalFit.bind(chart.legend)();
      this.height += 20;
    };
  },
};

const endpointPulsePlugin = {
  id: "endpointPulse",
  afterInit(chart: any) {
    const pulseState = {
      rafId: 0 as number | null,
      isDestroyed: false,
    };

    const animate = () => {
      if (pulseState.isDestroyed) return;
      chart.draw();
      pulseState.rafId = requestAnimationFrame(animate);
    };

    pulseState.rafId = requestAnimationFrame(animate);
    chart.$endpointPulse = pulseState;
  },
  afterDatasetsDraw(chart: any) {
    const { ctx } = chart;
    const time = Date.now();
    const cycleMs = 1500;
    const progress = (time % cycleMs) / cycleMs;
    const pulse = Math.sin(progress * Math.PI);

    chart.data.datasets.forEach((_: unknown, datasetIndex: number) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      const points = meta?.data;
      if (!points?.length) return;

      const point = points[points.length - 1];
      const x = point?.x;
      const y = point?.y;
      if (typeof x !== "number" || typeof y !== "number") return;

      const baseColor = chart.data.datasets[datasetIndex]?.borderColor as
        | string
        | undefined;
      if (!baseColor) return;

      ctx.save();
      ctx.globalAlpha = Math.max(0.1, pulse * 0.25);
      ctx.strokeStyle = baseColor;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(x, y, 5 + pulse, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    });
  },
  destroy(chart: any) {
    const pulseState = chart.$endpointPulse;
    if (!pulseState) return;

    pulseState.isDestroyed = true;
    if (pulseState.rafId) {
      cancelAnimationFrame(pulseState.rafId);
    }
  },
};

const options = (theme: "dark" | "light"): ChartOptions<"line"> => ({
  maintainAspectRatio: false,
  responsive: true,
  layout: {
    autoPadding: true,
    padding: {
      right: 44,
    },
  },
  elements: {
    point: {
      radius: (ctx) => (isLastPoint(ctx) ? 4 : 0),
      hoverRadius: (ctx) => (isLastPoint(ctx) ? 4 : 0),
      borderWidth: 0,
      pointStyle: "circle",
      hoverBorderWidth: 0,
    },
  },
  plugins: {
    legend: {
      position: "top",
      align: "start",
      maxWidth: 200,
      labels: {
        usePointStyle: true,
        boxWidth: 6,
        boxHeight: 6,
        pointStyle: "circle",
        padding: 16,
        color:
          theme === "dark"
            ? getThemeColor("gray-600")
            : getThemeColor("gray-700"),
        font: {
          family: getThemeFontSans(),
          size: remToPx(getThemeTextSize("sm")),
        },
      },
    },
  },
  scales: {
    y: {
      type: "linear",
      min: 0,
      max: 100,
      ticks: {
        display: false,
        stepSize: 20,
        callback: (value: number | string) => `${value}%`,
      },
      grid: {
        drawOnChartArea: false,
        drawTicks: false,
      },
      border: {
        display: false,
      },
    },
    x: {
      ticks: {
        autoSkip: false,
        maxRotation: 0,
        minRotation: 0,
        callback: function (_, index, ticks) {
          const total = ticks.length;
          if (
            index === Math.floor(total * 0.25) ||
            index === Math.floor(total * 0.5) ||
            index === Math.floor(total * 0.75)
          ) {
            return this.getLabelForValue(ticks[index].value);
          }
          return "";
        },
        color:
          theme === "dark"
            ? getThemeColor("gray-700")
            : getThemeColor("gray-500"),
        font: {
          family: getThemeFontSans(),
          size: remToPx(getThemeTextSize("xs")),
        },
      },
      grid: {
        drawOnChartArea: false,
        lineWidth: 0,
      },
    },
  },
  clip: false,
  events: [],
});

export { yAxisTopPlugin, legendSpacingPlugin, endpointPulsePlugin, options };
